import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { tenantsAPI, propertiesAPI, apartmentsAPI, usersAPI } from '../services/api';
import Modal from '../components/common/Modal';
import { toast } from '../components/common/Toast';
import {
  Users, Plus, Search, Edit2, UserMinus, RefreshCw,
  UserCheck, Phone, Mail, Home, Building2,
} from 'lucide-react';
import { statusClass, initials, avatarColor, fmt, errMsg } from '../utils/helpers';
import { usePermissions } from '../context/PermissionsContext';

const EMPTY = {
  name: '', email: '', phone: '', dob: '', moveIn: '',
  employer: '', income: '', creditScore: '',
  apartmentId: '',
  emergencyContact: { name: '', phone: '', relation: '' },
};

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="card" style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--ink-80)', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

export default function TenantsPage() {
  const { t }    = useTranslation();
  const { can }  = usePermissions();
  const canCreate = can('tenants', 'create');
  const canEdit   = can('tenants', 'edit');
  const [tenants, setTenants]         = useState([]);
  const [properties, setProperties]   = useState([]);
  const [allApts, setAllApts]         = useState([]);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [modal, setModal]             = useState(false);
  const [edit, setEdit]               = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [propFilter, setPropFilter]   = useState('');
  const [linkedUserId, setLinkedUserId] = useState('');
  const [saving, setSaving]           = useState(false);

  const formApts = propFilter
    ? allApts.filter(a => (a.propertyId?._id || a.propertyId)?.toString() === propFilter)
    : [];

  const load = useCallback(() => {
    setLoading(true);
    const q = {};
    if (search) q.search = search;
    Promise.all([
      tenantsAPI.getAll(q),
      propertiesAPI.getAll(),
      apartmentsAPI.getAll(),
      usersAPI.getAll({ role: 'tenant' }),
    ])
      .then(([tr, pr, ar, ur]) => {
        setTenants(tr.data.data);
        setProperties(pr.data.data);
        setAllApts(ar.data.data);
        setTenantUsers(ur.data.data);
      })
      .catch(() => toast.error(t('failedToLoad')))
      .finally(() => setLoading(false));
  }, [search, t]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  // Map tenantId -> linked user for quick lookup
  const userByTenantId = {};
  tenantUsers.forEach(u => {
    if (u.tenantId) userByTenantId[u.tenantId?.toString()] = u;
  });

  const openAdd = () => {
    setForm(EMPTY);
    setEdit(null);
    setPropFilter('');
    setLinkedUserId('');
    setModal(true);
  };

  const openEdit = ten => {
    setEdit(ten._id);
    const aptPropId = ten.apartmentId?.propertyId?._id || ten.apartmentId?.propertyId || '';
    setPropFilter(aptPropId?.toString() || '');
    const linked = userByTenantId[ten._id?.toString()];
    setLinkedUserId(linked?._id?.toString() || '');
    setForm({
      name:        ten.name || '',
      email:       ten.email || '',
      phone:       ten.phone || '',
      dob:         ten.dob  ? ten.dob.substring(0, 10) : '',
      moveIn:      ten.moveIn ? ten.moveIn.substring(0, 10) : '',
      employer:    ten.employer || '',
      income:      ten.income != null ? String(ten.income) : '',
      creditScore: ten.creditScore != null ? String(ten.creditScore) : '',
      apartmentId: (ten.apartmentId?._id || ten.apartmentId || '')?.toString(),
      emergencyContact: {
        name:     ten.emergencyContact?.name || '',
        phone:    ten.emergencyContact?.phone || '',
        relation: ten.emergencyContact?.relation || '',
      },
    });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.name) return toast.warning(t('fullName') + ' ' + t('required'));
    setSaving(true);
    try {
      const data = { ...form };
      if (!data.apartmentId)  delete data.apartmentId;
      if (data.income === '')  delete data.income;
      if (data.creditScore === '') delete data.creditScore;
      if (!data.dob)           delete data.dob;
      if (!data.moveIn)        delete data.moveIn;
      if (!data.emergencyContact.name && !data.emergencyContact.phone) {
        delete data.emergencyContact;
      }

      let tenantId = edit;
      if (edit) {
        await tenantsAPI.update(edit, data);
        toast.success(t('tenantUpdated'));
      } else {
        const res = await tenantsAPI.create(data);
        tenantId = res.data.data._id;
        toast.success(t('tenantCreated'));
      }

      // Handle user account linking
      const prevLinked = edit ? userByTenantId[edit?.toString()] : null;
      const prevLinkedId = prevLinked?._id?.toString();

      if (linkedUserId && linkedUserId !== prevLinkedId) {
        // Unlink old user if different
        if (prevLinkedId) {
          await usersAPI.update(prevLinkedId, { tenantId: null });
        }
        await usersAPI.update(linkedUserId, { tenantId: tenantId });
      } else if (!linkedUserId && prevLinkedId) {
        await usersAPI.update(prevLinkedId, { tenantId: null });
      }

      setModal(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = (id, name) => {
    toast.confirm(
      `${t('deactivateTenant')} ${name}?`,
      async () => {
        try {
          await tenantsAPI.delete(id);
          toast.success(t('tenantDeactivated'));
          load();
        } catch (err) { toast.error(errMsg(err)); }
      },
      t('deactivate')
    );
  };

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const ec  = (k, v) => setForm(f => ({ ...f, emergencyContact: { ...f.emergencyContact, [k]: v } }));

  const active     = tenants.filter(tn => tn.isActive);
  const withLease  = tenants.filter(tn => tn.isActive && tn.leaseId);
  const withUser   = tenants.filter(tn => tn.isActive && userByTenantId[tn._id?.toString()]);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('tenants')}</h1>
          <p className="page-subtitle">{active.length} {t('activeTenants')}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-secondary btn-icon" onClick={load}><RefreshCw size={16} /></button>
          {canCreate && <button className="btn btn-primary" onClick={openAdd}><Plus size={16} />{t('addTenant')}</button>}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-4 mb-6">
        <StatCard icon={Users}      color="var(--brand)"   label={t('totalTenants')}  value={tenants.length} />
        <StatCard icon={UserCheck}  color="var(--emerald)" label={t('activeTenants')} value={active.length} />
        <StatCard icon={Home}       color="var(--violet)"  label={t('withLease')}     value={withLease.length} />
        <StatCard icon={Building2}  color="var(--amber)"   label={t('linkedAccounts')} value={withUser.length} />
      </div>

      {/* Search */}
      <div className="card mb-4" style={{ padding: '14px 16px' }}>
        <div className="search-bar">
          <Search size={15} color="var(--ink-20)" />
          <input
            placeholder={t('searchTenants')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div style={{ overflowX: 'auto' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-40)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} />
            {t('loading')}
          </div>
        ) : tenants.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--ink-40)' }}>{t('noTenantsFound')}</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width:1, whiteSpace:'nowrap' }}></th>
                <th>{t('tenant')}</th>
                <th>{t('contact')}</th>
                <th>{t('property')} / {t('unit')}</th>
                <th>{t('moveIn')}</th>
                <th>{t('lease')}</th>
                <th>{t('linkedUser')}</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(ten => {
                const linked   = userByTenantId[ten._id?.toString()];
                const propName = ten.apartmentId?.propertyId?.name || '—';
                const unit     = ten.apartmentId?.unit;
                const leaseStatus = ten.leaseId?.status;
                return (
                  <tr key={ten._id}>
                    <td className="table-actions">
                      <div style={{ display: 'flex', gap: 6 }}>
                        {canEdit && <button className="btn btn-ghost btn-icon btn-sm" onClick={() => openEdit(ten)} title={t('edit')}>
                          <Edit2 size={14} />
                        </button>}
                        {canEdit && ten.isActive && (
                          <button className="btn btn-ghost btn-icon btn-sm" style={{ color: 'var(--rose)' }}
                            onClick={() => deactivate(ten._id, ten.name)} title={t('deactivate')}>
                            <UserMinus size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div className="avatar avatar-sm"
                          style={{ background: avatarColor(ten.name), color: '#fff', fontWeight: 700 }}>
                          {initials(ten.name)}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{ten.name}</div>
                          <span className={`badge ${ten.isActive ? 'badge-active' : 'badge-neutral'}`} style={{ fontSize: 10 }}>
                            {ten.isActive ? t('active') : t('inactive')}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, color: 'var(--ink-60)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <span><Mail size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />{ten.email || '—'}</span>
                        <span><Phone size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />{ten.phone || '—'}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>
                        <div style={{ fontWeight: 500 }}>{propName}</div>
                        <div style={{ color: 'var(--ink-40)', fontSize: 12 }}>{unit ? `Unit ${unit}` : '—'}</div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--ink-60)' }}>
                      {ten.moveIn
                        ? new Date(ten.moveIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—'}
                    </td>
                    <td>
                      {leaseStatus
                        ? <span className={`badge ${statusClass(leaseStatus)}`}>{leaseStatus}</span>
                        : <span style={{ color: 'var(--ink-30)', fontSize: 13 }}>—</span>}
                    </td>
                    <td>
                      {linked ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <div className="avatar"
                            style={{ width: 22, height: 22, fontSize: 9, background: avatarColor(linked.name), color: '#fff', fontWeight: 700, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            {initials(linked.name)}
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--ink-60)' }}>{linked.name}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--ink-30)' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={edit ? t('editTenant') : t('addTenant')}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setModal(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? t('saving') : t('save')}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Personal Info */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {t('personalInfo')}
            </div>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('fullName')} *</label>
                <input className="form-input" value={form.name}
                  onChange={e => fld('name', e.target.value)} placeholder={t('enterFullName')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('email')}</label>
                <input className="form-input" type="email" value={form.email}
                  onChange={e => fld('email', e.target.value)} placeholder={t('enterEmail')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('phone')}</label>
                <input className="form-input" value={form.phone}
                  onChange={e => fld('phone', e.target.value)} placeholder="(555) 000-0000" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('dateOfBirth')}</label>
                <input className="form-input" type="date" value={form.dob}
                  onChange={e => fld('dob', e.target.value)} />
              </div>
            </div>
          </section>

          {/* Assignment */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {t('assignment')}
            </div>
            <div className="grid grid-3" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('property')}</label>
                <select className="form-input" value={propFilter}
                  onChange={e => { setPropFilter(e.target.value); fld('apartmentId', ''); }}>
                  <option value="">{t('select')}</option>
                  {properties.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('unit')}</label>
                <select className="form-input" value={form.apartmentId}
                  onChange={e => fld('apartmentId', e.target.value)} disabled={!propFilter}>
                  <option value="">{t('select')}</option>
                  {formApts.map(a => (
                    <option key={a._id} value={a._id}>
                      {a.unit}{a.bedrooms ? ` · ${a.bedrooms}bd` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('moveInDate')}</label>
                <input className="form-input" type="date" value={form.moveIn}
                  onChange={e => fld('moveIn', e.target.value)} />
              </div>
            </div>
          </section>

          {/* Financial Info */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {t('financialInfo')}
            </div>
            <div className="grid grid-3" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('employer')}</label>
                <input className="form-input" value={form.employer}
                  onChange={e => fld('employer', e.target.value)} placeholder={t('employerPlaceholder')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('annualIncome')}</label>
                <input className="form-input" type="number" min="0" value={form.income}
                  onChange={e => fld('income', e.target.value)} placeholder="50000" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('creditScore')}</label>
                <input className="form-input" type="number" min="300" max="850" value={form.creditScore}
                  onChange={e => fld('creditScore', e.target.value)} placeholder="700" />
              </div>
            </div>
          </section>

          {/* Emergency Contact */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {t('emergencyContact')}
            </div>
            <div className="grid grid-3" style={{ gap: 12 }}>
              <div className="form-group">
                <label className="form-label">{t('contactName')}</label>
                <input className="form-input" value={form.emergencyContact.name}
                  onChange={e => ec('name', e.target.value)} placeholder={t('enterFullName')} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('contactPhone')}</label>
                <input className="form-input" value={form.emergencyContact.phone}
                  onChange={e => ec('phone', e.target.value)} placeholder="(555) 000-0000" />
              </div>
              <div className="form-group">
                <label className="form-label">{t('relation')}</label>
                <input className="form-input" value={form.emergencyContact.relation}
                  onChange={e => ec('relation', e.target.value)} placeholder={t('relationPlaceholder')} />
              </div>
            </div>
          </section>

          {/* User Account Link */}
          <section>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-40)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>
              {t('userAccount')}
            </div>
            <div className="form-group">
              <label className="form-label">{t('linkUserAccount')}</label>
              <select className="form-input" value={linkedUserId} onChange={e => setLinkedUserId(e.target.value)}>
                <option value="">{t('noLinkedAccount')}</option>
                {tenantUsers.map(u => {
                  const alreadyTaken = u.tenantId && u.tenantId?.toString() !== edit?.toString();
                  return (
                    <option key={u._id} value={u._id} disabled={alreadyTaken}>
                      {u.name} ({u.email}){alreadyTaken ? ` — ${t('alreadyLinked')}` : ''}
                    </option>
                  );
                })}
              </select>
              <p style={{ fontSize: 11, color: 'var(--ink-30)', marginTop: 4 }}>{t('linkUserAccountHint')}</p>
            </div>
          </section>

        </div>
      </Modal>
    </div>
  );
}
